import './apartment.scss';

/* eslint-disable */
import * as THREE from 'three';
import Component from '../../components/component/component';

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';

import glassMaterialData from './import/glass-material-grey.json';

export default class Apartment extends Component {
    static initSelector: string = '.apartment-3d-container';

    scene: THREE.Scene;
    renderer: THREE.WebGLRenderer;
    camera: THREE.PerspectiveCamera;
    controls: OrbitControls;
    glassMaterial: THREE.MeshPhysicalMaterial;
    raycaster: THREE.Raycaster;
    mouse: THREE.Vector2;
    orbitMouse: THREE.Vector2;
    sprite: THREE.Sprite;
    hoveringObject: null|ReturnType<typeof Object>;
    targetCameraPosition: THREE.Vector3;
    gsapMovement: GSAPTween;
    rotationTimer: number|null;

    constructor(target: HTMLElement) {
        super(target);

        this.init();
    }

    init() {
        this.orbitMouse = new THREE.Vector2();

        this.setupScene();
        this.setupRenderer();
        this.setupCameraAndControls();
        this.setupLighting();
        this.setupRaycaster();

        this.loadGlassMaterial();
        this.loadModelsAndMaterials();

        window.addEventListener('resize', () => this.onWindowResize());
    }

    setupScene(): void {
        this.scene = new THREE.Scene();
    }

    setupRenderer(): void {
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
        });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(this.element.innerWidth(), this.element.innerHeight());
        this.renderer.setClearColor(0xffffff, 0);
        this.element.append(this.renderer.domElement);

        this.renderer.setAnimationLoop(() => this.animate());
        this.renderer.domElement.addEventListener('mousedown', () => this.element.addClass('apartment-3d-container--touched'));
        this.renderer.domElement.addEventListener('touchstart', () => this.element.addClass('apartment-3d-container--touched'));
    }

    setupCameraAndControls(): void {
        // Camera
        this.camera = new THREE.PerspectiveCamera(50, this.element.innerWidth() / this.element.innerHeight(), 0.1, 2000);
        this.camera.position.set(0, 0, 0);

        // Controls
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.maxPolarAngle = Math.PI / 2;
        this.controls.enablePan = false;
        this.controls.enableZoom = false;
        this.controls.autoRotate = false;
        this.controls.autoRotateSpeed = 0.35;
        this.controls.maxAzimuthalAngle = this.controls.getAzimuthalAngle()

        const rotatationTimeout = 3500;

        this.rotationTimer = window.setTimeout(() => {
            this.controls.autoRotate = true;
        }, rotatationTimeout);

        this.controls.addEventListener('start', () => {
            if (this.rotationTimer) {
                window.clearTimeout(this.rotationTimer);
                this.rotationTimer = null;
            }

            this.controls.autoRotate = false;
        });

        this.controls.addEventListener('end', () => {
            this.rotationTimer = window.setTimeout(() => {
                this.controls.autoRotate = true;
            }, rotatationTimeout);
        });
    }

    setupLighting(): void {
        const ambientLight: THREE.AmbientLight = new THREE.AmbientLight(0xffffff, 0.5);

        this.scene.add(ambientLight);

        const directionalLight: THREE.DirectionalLight = new THREE.DirectionalLight(0xffffff, 1);

        directionalLight.position.set(0, 1, 1).normalize();
        this.scene.add(directionalLight);
    }

    setupRaycaster(): void {
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
    }

    loadGlassMaterial(): void {
        this.glassMaterial = new THREE.MeshPhysicalMaterial({
            color: glassMaterialData.color || 0xffffff,
            opacity: glassMaterialData.opacity || 0.5,
            transparent: glassMaterialData.transparent || true,
            roughness: 0,
            metalness: 0,
            clearcoat: 1,
            clearcoatRoughness: 0,
            reflectivity: glassMaterialData.reflectivity || 0.9,
            refractionRatio: 0.98,
            ior: 1.5,
        });
    }

    loadModelsAndMaterials(): void {
        const loader: GLTFLoader = new GLTFLoader();
        const dracoLoader: DRACOLoader = new DRACOLoader();

        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/')
        loader.setDRACOLoader(dracoLoader)

        loader.load(
            this.element.data('model-url'),
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            (gltf: any) => {
                const model: THREE.Group = gltf.scene;

                const box = new THREE.Box3().setFromObject(model)

                const boxCenter = box.getCenter(new THREE.Vector3())
                const boxSize = box.getSize(new THREE.Vector3())

                model.position.set(-boxCenter.x, boxSize.y / 2 - boxCenter.y, -boxCenter.z);

                this.scene.add(model);

                this.applyMaterialsToModel(model);
                this.adjustCameraToFitModel(model);
                this.hideLoaderShowCanvas();
            },
            (xhr: ProgressEvent) => this.updateLoadingBar(xhr),
        );
    }

    applyMaterialsToModel(model: THREE.Group): void {
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        model.traverse((child: any) => {
            if (child.isMesh && child.name.startsWith('GLASS')) {
                child.material = this.glassMaterial;
            } else if (child.name.startsWith('CENTERPOINT')) {
                const centerPosition = new THREE.Vector3();
                this.targetCameraPosition = child.getWorldPosition(centerPosition);

                child.visible = false;
            }
        });
    }

    // Adjust camera position and controls to focus on the loaded model
    adjustCameraToFitModel(model: THREE.Group): void {
        const box: THREE.Box3 = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        const distance: number = size.length()

        if (this.targetCameraPosition) {
            this.camera.position.set(this.targetCameraPosition.x, this.targetCameraPosition.y + (distance * 1.2), this.targetCameraPosition.z);
            this.camera.lookAt(this.targetCameraPosition);

            this.controls.target.copy(this.targetCameraPosition);
            this.controls.update();
        } else {
            this.camera.position.set(center.x, center.y + (distance * 1.2), center.z);
            this.camera.lookAt(center);

            this.controls.target.copy(center);
            this.controls.update();
        }
    }

    updateLoadingBar(xhr: ProgressEvent): void {
        //
    }

    // Hide loader and show canvas
    hideLoaderShowCanvas(): void {
        this.element.removeClass('apartment-3d-container--loading');
        this.renderer.domElement.style.display = 'block';
    }

    onWindowResize(): void {
        this.camera.aspect = this.element.innerWidth() / this.element.innerHeight();
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.element.innerWidth(), this.element.innerHeight());
    }

    animate(): void {
        this.controls.update();
        this.renderer.render(this.scene, this.camera);
    }
}
